import { normalizeBoard } from 'api/normalize';
import { Action, actionCreator } from 'util/actions';

// Action types
import {
  LOAD_BOARD_SUCCESS,
  LOAD_CARD_SUCCESS,
  LOAD_CLOSED_BOARDS_SUCCESS,
  LOAD_MEMBER_CARDS_SUCCESS,
  LOAD_MISSING_BOARDS_SUCCESS,
  LOAD_MY_BOARDS_SUCCESS,
  LoadBoardSuccessAction,
  LoadCardSuccessAction,
  LoadClosedBoardsSuccessAction,
} from 'modules/loaders';

import {
  GetSearchSuggestionsAction,
  PERFORM_SEARCH_SUCCESS,
  PerformSearchSuccessAction,
  SET_SEARCHED_SUGGESTIONS,
} from './search';

import { SOCKET_BOARD, SocketBoardAction } from 'modules/sockets';
import {
  CREATE_BOARD_SUBMIT_DONE,
  CreateBoardSubmitDoneAction,
} from 'modules/state/ui/create-menu';
import { BoardModel, BoardResponse } from 'types';
import { createReducer } from 'util/reducers';
import uniqBy from 'util/uniqBy';
import updateModelList from './util/updateModelList';

export type BoardState = BoardModel[];
const SET_BOARD_NAME = Symbol('models/SET_BOARD_NAME');
export const CREATE_BOARD = Symbol('models/CREATE_BOARD');
const ADD_MEMBER_TO_BOARD = Symbol('models/ADD_MEMBER_TO_BOARD');
export const REMOVE_MEMBER_FROM_BOARD = Symbol(
  'models/REMOVE_MEMBER_FROM_BOARD',
);
export const SET_BOARD_CLOSED = Symbol('models/SET_BOARD_CLOSED');
export const DELETE_BOARD = Symbol('models/DELETE_BOARD');

type SetBoardNameAction = Action<typeof SET_BOARD_NAME, BoardResponse>;
type CreateBoardAction = Action<typeof CREATE_BOARD, BoardResponse>;
type AddMemberToBoardAction = Action<typeof ADD_MEMBER_TO_BOARD, BoardResponse>;
export type RemoveMemberFromBoardAction = Action<
  typeof REMOVE_MEMBER_FROM_BOARD,
  { idBoard: string; idMember: string }
>;
export type SetBoardClosedAction = Action<
  typeof SET_BOARD_CLOSED,
  BoardResponse
>;
export type DeleteBoardAction = Action<typeof DELETE_BOARD, string>;

export const setBoardNameAction = actionCreator<SetBoardNameAction>(
  SET_BOARD_NAME,
);
export const createBoardAction = actionCreator<CreateBoardAction>(CREATE_BOARD);
export const addMemberToBoardAction = actionCreator<AddMemberToBoardAction>(
  ADD_MEMBER_TO_BOARD,
);
export const removeMemberFromBoardAction = actionCreator<
  RemoveMemberFromBoardAction
>(REMOVE_MEMBER_FROM_BOARD);
export const setBoardClosedAction = actionCreator<SetBoardClosedAction>(
  SET_BOARD_CLOSED,
);
export const deleteBoardAction = actionCreator<DeleteBoardAction>(DELETE_BOARD);

// Reducer
const initialState: BoardState = [];

export default createReducer(initialState, {
  [CREATE_BOARD_SUBMIT_DONE](state, { payload }: CreateBoardSubmitDoneAction) {
    return payload
      ? uniqBy([...state, normalizeBoard(payload)], (board) => board.id)
      : state;
  },
  // tslint:disable-next-line:no-any
  [LOAD_MEMBER_CARDS_SUCCESS](state, { payload }: any) {
    return uniqBy<BoardModel>(
      payload.boards.map(normalizeBoard).concat(state) as BoardModel[],
      (board) => board.id,
    );
  },
  // tslint:disable-next-line:no-any
  [LOAD_MY_BOARDS_SUCCESS](state, { payload }: any) {
    return uniqBy<BoardModel>(
      payload.boards.map(normalizeBoard).concat(state) as BoardModel[],
      (board) => board.id,
    );
  },
  // tslint:disable-next-line:no-any
  [LOAD_MISSING_BOARDS_SUCCESS](state, { payload }: any) {
    return uniqBy<BoardModel>(
      payload.map(normalizeBoard).concat(state) as BoardModel[],
      (board) => board.id,
    );
  },
  [LOAD_BOARD_SUCCESS](state, { payload }: LoadBoardSuccessAction) {
    return state
      .filter((board) => board.id !== payload.id)
      .concat(normalizeBoard(payload));
  },
  [LOAD_CARD_SUCCESS](state, { payload }: LoadCardSuccessAction) {
    return payload.board
      ? uniqBy([...state, normalizeBoard(payload.board)], (board) => board.id)
      : state;
  },
  [PERFORM_SEARCH_SUCCESS](state, { payload }: PerformSearchSuccessAction) {
    const { boards = [], cards = [] } = payload;

    return uniqBy(
      [
        ...boards.map((board) => normalizeBoard(board)),
        ...cards.map(
          (card) => (card.board ? normalizeBoard(card.board) : null),
        ),
        ...state,
      ],
      (board) => board && board.id,
    );
  },
  [SET_SEARCHED_SUGGESTIONS](state, { payload }: GetSearchSuggestionsAction) {
    const { boards = [] } = payload;

    return uniqBy(
      [...state, ...boards.map((board) => normalizeBoard(board))],
      (board) => board && board.id,
    );
  },
  [SOCKET_BOARD](state, { payload }: SocketBoardAction) {
    return updateModelList(state, payload, normalizeBoard);
  },
  [SET_BOARD_NAME](state, { payload }: SetBoardNameAction) {
    const { id, shortLink, name } = normalizeBoard(payload);
    const modifiedBoard = id
      ? state.find((board) => board.id === id)
      : state.find((board) => board.shortLink === shortLink);

    return state.map(
      (board) => (board !== modifiedBoard ? board : { ...modifiedBoard, name }),
    );
  },
  [CREATE_BOARD](state, { payload }: CreateBoardAction) {
    return [...state, normalizeBoard(payload)];
  },
  [ADD_MEMBER_TO_BOARD](state, { payload }: AddMemberToBoardAction) {
    const { id, memberships } = normalizeBoard(payload);

    return state.map(
      (board) =>
        board.id !== id
          ? board
          : {
              ...board,
              memberships,
            },
    );
  },
  [REMOVE_MEMBER_FROM_BOARD](state, { payload }: RemoveMemberFromBoardAction) {
    const { idBoard, idMember } = payload;

    return state.map(
      (board) =>
        board.id !== idBoard
          ? board
          : {
              ...board,
              memberships: (board.memberships || []).filter(
                (membership) => membership.idMember !== idMember,
              ),
            },
    );
  },
  [SET_BOARD_CLOSED](state, { payload }: SetBoardClosedAction) {
    const { id, shortLink, closed } = normalizeBoard(payload);
    const modifiedBoard = id
      ? state.find((board) => board.id === id)
      : state.find((board) => board.shortLink === shortLink);

    return state.map(
      (board) =>
        board !== modifiedBoard ? board : { ...modifiedBoard, closed },
    );
  },
  [DELETE_BOARD](state, { payload }: DeleteBoardAction) {
    const deletedBoard = state.find((board) => board.id === payload);

    return state.filter((board) => board !== deletedBoard);
  },
  [LOAD_CLOSED_BOARDS_SUCCESS](
    state,
    { payload = [] }: LoadClosedBoardsSuccessAction,
  ) {
    return uniqBy(
      [...payload.map((board) => normalizeBoard(board)), ...state],
      (board) => board && board.id,
    );
  },
});



// WEBPACK FOOTER //
// ./app/gamma/src/modules/state/models/boards.ts